import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import AceEditor from 'react-ace';
import ace from 'ace-builds';
import 'ace-builds/webpack-resolver';
// 按需导入模式和主题
import 'ace-builds/src-noconflict/mode-c_cpp';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';
// 添加 vim 模式支持
import 'ace-builds/src-noconflict/keybinding-vim';

import { Message, SortType } from '../types';
import { getMessages, sendMessage, updateMessageExpiration, uploadImage, uploadFile, getHoleInfo } from '../services/api';
import MessageItem from '../components/MessageItem';
import { PaperAirplaneIcon, CodeBracketIcon, PhotoIcon, ArrowDownIcon, PaperClipIcon, ArrowPathIcon, QrCodeIcon, ChevronUpIcon, ChevronDownIcon, CurrencyBangladeshiIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { fmtNumber } from '../utils';

// 首先添加 Hole 类型定义
interface Hole {
    id: string;
    name: string;
    pwd: string;
    total_messages: number;
    total_bytes: number;
    valid_messages: number;
    valid_bytes: number;
}

const Chat: React.FC = () => {
    const { holeId } = useParams<{ holeId: string }>();
    const [hole, setHole] = useState<Hole | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [content, setContent] = useState('');
    const [isCode, setIsCode] = useState(false);
    const [sortType, setSortType] = useState<SortType>('created_desc');
    const [loading, setLoading] = useState(false);
    const [hasNewMessages, setHasNewMessages] = useState(0);
    const [showNewMessageButton, setShowNewMessageButton] = useState(false);
    const [messageAreaHeight, setMessageAreaHeight] = useState(400); // 初始消息区域高度
    const [inputAreaHeight, setInputAreaHeight] = useState(138); // 初始输入区域高度
    const [showQRCode, setShowQRCode] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const lastMessageIdRef = useRef<string | null>(null);

    const [currentSelectExpirationMessageId, setCurrentSelectExpirationMessageId] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    //const lastMessageCountRef = useRef(0);
    const [isExpanded, setIsExpanded] = useState(false);  // 是否展开全屏
    const [inputRows, setInputRows] = useState(1);  // 当前输入行数
    const [isFocused, setIsFocused] = useState(false);  // 输入框是否处于编辑状态
    const [isMobile, setIsMobile] = useState(false);  // 添加移动设备检测状态
    const messagesRef = useRef<Message[]>(messages); // 创建一个 ref 来存储 messages

    // 加载更多历史消息
    const loadMoreMessages = async () => {
        //console.log("loadMoreMessage...");
        if (!holeId || !messages.length || isLoadingMore) return;
        try {
            setIsLoadingMore(true);
            const oldestMessage = messages[0];
            const response = await getMessages(holeId, sortType, {
                beforeId: oldestMessage.id,
                limit: 20
            });
            setMessages(prev => {
                return [...response.messages.reverse(), ...prev];
                //const messageMap = new Map([...prev, ...response.messages].map(msg => [msg.id, msg]));
                // const messages = Array.from(messageMap.values());
                // return sortMessages(messages, sortType);
            });
        } catch (err) {
            console.error('加载更多消息失败:', err);
        } finally {
            setIsLoadingMore(false);
        }
    };

    const checkIfNeedScrollToBottom = () => {
        if (messagesEndRef.current && containerRef.current) {
            const { offsetTop } = messagesEndRef.current;
            //console.log(offsetTop);

            const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
            //console.log(scrollTop + clientHeight, scrollHeight);
            //表示目前message停留在最底部，收到新消息需要滚动到底部，
            //否则表示用户手动滚动过，不要滚动到底部；
            //自己发送消息，滚动到底部
            return offsetTop <= scrollTop + clientHeight;
        }
        return false;
    }

    // 检查新消息
    const checkNewMessages = (newMessages: Message[]) => {
        if (!newMessages.length) return;
        if (containerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
            setShowNewMessageButton(scrollTop + clientHeight < scrollHeight);
        }

        // console.log('检查新消息:', newMessages.length, lastMessageCountRef.current);
        // if (newMessages.length > lastMessageCountRef.current) {
        //    console.log('有新消息！')
        //     setHasNewMessages(newMessages.length - lastMessageCountRef.current);
        // }
        // lastMessageCountRef.current = newMessages.length;
    };

    // 获取最新消息
    const fetchMessages = async () => {
        if (!holeId) return;
        try {
            //console.log('fetchMessages');
            const response = await getMessages(holeId, sortType, { limit: 30 });
            setMessages(response.messages.reverse());
            if (response.messages.length > 0) {
                lastMessageIdRef.current = response.messages[0].id;
            }
            scrollToBottom();
            checkNewMessages(response.messages);
        } catch (err) {
            console.error('获取消息失败:', err);
        }
    };

    // 在 useEffect 中更新 messagesRef
    useEffect(() => {
        messagesRef.current = messages; // 每次 messages 更新时，更新 ref 的值
    }, [messages]);


    // 获取新消息（轮询）
    const fetchNewMessages = async () => {
        if (!holeId) return;
        //console.log('fetchNewMessages', messagesRef.current.length); // 使用 ref 获取最新的 messages

        if (!messagesRef.current.length) {
            await fetchMessages();
            return;
        }

        try {
            //console.log('fetchNewMessages');
            const latestMessage = messagesRef.current[messagesRef.current.length - 1]; // 使用 ref 获取最新的消息
            const response = await getMessages(holeId, sortType, {
                lastId: latestMessage.id,
                limit: 30
            });
            if (response.messages.length > 0) {
                const news = [...messagesRef.current, ...response.messages];
                setMessages(news);

                checkNewMessages(response.messages);
            }
        } catch (err) {
            console.error('获取新消息失败:', err);
        }
    };

    // 轮询获取新消息
    useEffect(() => {
        if (!holeId) return;
        console.log('轮询获取新消息:', holeId);

        const doFetchMessages = async () => {
            await fetchMessages();
            let interval: NodeJS.Timeout | null = null;
            const debounceFetchNewMessages = () => {
                console.log('debounceFetchNewMessages', new Date().getTime().toString(), interval);
                if (interval) return;
                interval = setTimeout(() => {
                    fetchNewMessages();
                    console.log('...', new Date().getTime().toString(), interval);
                    if (interval) clearTimeout(interval);
                    interval = null;
                    debounceFetchNewMessages();
                }, 5000);
            };
            debounceFetchNewMessages();
            return () => {
                if (interval) clearTimeout(interval);
            };
        };

        doFetchMessages();
    }, [holeId]);

    // 修改获取虫洞信息的函数
    const fetchHoleInfo = async () => {
        if (!holeId) return;
        try {
            const data = await getHoleInfo(holeId);
            setHole(data);
        } catch (error) {
            console.error('获取虫洞信息失败:', error);
        }
    };

    // 在组件挂载时获取虫洞信息
    useEffect(() => {
        fetchHoleInfo();
    }, []);

    // 处理新消息提示点击
    const handleNewMessageClick = () => {
        scrollToBottom();
        setShowNewMessageButton(false);
    };

    // 处理滚动事件
    const handleScroll = () => {
        if (containerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

            //console.log(scrollTop + clientHeight, scrollHeight);
            if (scrollTop + clientHeight >= scrollHeight)
                setShowNewMessageButton(false);
            // if (scrollTop + clientHeight >= scrollHeight)
            //     setHasNewMessages(0);

            // 滚动到顶部时加载更多
            if (scrollTop === 0) {
                loadMoreMessages();
            }
        }
    };

    const initAreaHeight = () => {
        setInputAreaHeight(inputAreaHeight);
        setMessageAreaHeight(window.innerHeight - inputAreaHeight - 64);
    };

    // 滚动到底部
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            //console.log("scrollToBottom...");
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            setShowNewMessageButton(false);
        }
    };


    useEffect(() => {
        initAreaHeight();

        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll, initAreaHeight]);



    // 发送消息
    const handleSend = async () => {
        if (!content.trim() || !holeId || loading) return;
        try {
            setLoading(true);
            await sendMessage(holeId, content, isCode ? 'code' : 'text');
            setContent('');
            setIsCode(false);
            // const response = await getMessages(holeId, sortType);
            // setMessages(response.messages);
            await fetchNewMessages();
            toggleExpand(true);

            scrollToBottom();
        } catch (err) {
            console.error('发送消息失败:', err);
        } finally {
            setLoading(false);
        }
    };

    // 处理键盘事件
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSend();
        }
    };

    // 处理图片上传
    const handleImageUpload = async (file: File) => {
        if (!holeId || loading) return;

        if (file.size > 500 * 1024 * 1024) {
            alert('文件大小不能超过5MB');
            return;
        }
        try {
            setLoading(true);
            await uploadImage(holeId, file);

            await fetchNewMessages();
            toggleExpand(true);

            scrollToBottom();

        } catch (err) {
            console.error('上传图片失败:', err);
        } finally {
            setLoading(false);
        }
    };

    // 处理文件上传
    const handleFileUpload = async (file: File) => {
        if (!holeId || loading) return;

        // 检查文件大小
        if (file.size > 500 * 1024 * 1024) {
            alert('文件大小不能超过5MB');
            return;
        }

        try {
            setLoading(true);
            await uploadFile(holeId, file);

            await fetchNewMessages();
            toggleExpand(true);

            scrollToBottom();
        } catch (err: any) {
            if (err.response?.status === 413) {
                alert('文件大小超过限制');
            } else {
                console.error('上传文件失败:', err);
                alert('上传文件失败: ' + (err.response?.data?.error || '未知错误'));
            }
        } finally {
            setLoading(false);
        }
    };

    // 处理文件选择
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileUpload(file);
        }
        e.target.value = ''; // 清空选择，允许重复选择同一文件
    };

    // 处理图片选择
    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleImageUpload(file);
        }
        e.target.value = '';
    };

    // 处理粘贴事件
    const handlePaste = async (e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                e.preventDefault();
                const file = items[i].getAsFile();
                if (file) {
                    await handleImageUpload(file);
                }
                break;
            }
        }
    };

    // 处理拖放事件
    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        const files = e.dataTransfer.files;

        for (let i = 0; i < files.length; i++) {
            if (files[i].type.indexOf('image') !== -1) {
                await handleImageUpload(files[i]);
                break;
            }
        }
    };

    // 处理拖动分割线
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        const windowHeight = window.innerHeight - 64; // 减去头部导航的高度
        const initialMessageHeight = messageAreaHeight;
        const initialY = e.clientY;

        const onMouseMove = (e: MouseEvent) => {
            const deltaY = initialY - e.clientY;
            const newMessageHeight = Math.max(200, initialMessageHeight - deltaY);
            const newInputHeight = Math.max(100, windowHeight - newMessageHeight);

            // 确保两个区域的总高度不超过可用空间
            if (newMessageHeight + newInputHeight <= windowHeight) {
                setMessageAreaHeight(newMessageHeight);
                setInputAreaHeight(newInputHeight);
            }
        };

        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    // 添加排序函数
    const sortMessages = (messages: Message[], sortType: SortType) => {
        return [...messages].sort((a, b) => {
            switch (sortType) {
                case 'created_asc':
                    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                case 'created_desc':
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                case 'expire_asc':
                    return new Date(a.expire_at).getTime() - new Date(b.expire_at).getTime();
                case 'expire_desc':
                    return new Date(b.expire_at).getTime() - new Date(a.expire_at).getTime();
                default:
                    return 0;
            }
        });
    };

    // 修改排序变化的处理
    useEffect(() => {
        // 排序变化时重排序现有消息
        setMessages(prev => sortMessages(prev, sortType));
    }, [sortType]);

    // 计算输入行数
    const calculateRows = (text: string) => {
        const lines = text.split('\n');
        const lineCount = lines.reduce((count, line) => {
            return count + Math.ceil(line.length / 30);  // 假设行约30个字符
        }, 0);
        return Math.min(3, Math.max(1, lineCount));
    };

    // 处理输入变化
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newContent = e.target.value;
        setContent(newContent);
        if (!isExpanded) {
            setInputRows(calculateRows(newContent));
        }
    };

    // 处理展开/收缩
    const toggleExpand = (forceClose: any) => {
        //console.log("toggleExpand:",forceClose,content);
        let cls = false;
        if (forceClose === true)
            cls = false;
        else
            cls = !isExpanded;

        setIsExpanded(cls);
        if (cls) {
            setInputAreaHeight(window.innerHeight - 64);  // 展开为全屏
        } else {

            //console.log("toggleExpand:1",forceClose,content);
            setInputAreaHeight(138);  // 恢复默认高度
            setInputRows(calculateRows(content));
        }
    };

    // 检测设备类型
    useEffect(() => {
        const checkDevice = () => {
            setIsMobile(window.innerWidth < 640);  // 使用 Tailwind 的 sm 断点
        };

        checkDevice();
        window.addEventListener('resize', checkDevice);
        return () => window.removeEventListener('resize', checkDevice);
    }, []);

    // 修改输入框高度计算逻辑
    const calculateInputHeight = (rows: number) => {
        // if (isMobile) {
        //     return Math.max(24 + (rows - 1) * 20, 38);  // 移动端根据行数计算
        // }
        return inputAreaHeight - 32;  // 桌面端保持原有逻辑
    };

    // 在复制内容的函数中调用提示
    const copyContent = () => {
        const msg = window.location.href;
        navigator.clipboard.writeText(msg);
    };

    // 修改工具栏渲染逻辑
    const renderToolbar = () => {
        const commonButtons = (
            <>
                <button
                    onClick={() => setIsCode(!isCode)}
                    className={clsx(
                        "p-1.5 rounded-lg transition-colors",
                        isCode ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-100"
                    )}
                >
                    <CodeBracketIcon className="w-5 h-5" />
                </button>

                {(!isMobile || !content) && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".txt,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.py,.js,.jsx,.ts,.tsx,.java,.c,.cpp,.h,.hpp,.cs,.php,.html,.css"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <PaperClipIcon className="w-5 h-5" />
                        </button>

                        <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageSelect}
                            className="hidden"
                        />
                        <button
                            onClick={() => imageInputRef.current?.click()}
                            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <PhotoIcon className="w-5 h-5" />
                        </button>
                    </>
                )}

                <button
                    onClick={handleSend}
                    disabled={loading || !content.trim()}
                    className={clsx(
                        isMobile
                            ? "py-1.5 px-3 text-xs"
                            : "py-2 px-4 text-base",
                        "rounded-lg text-white font-medium transition-colors",
                        loading || !content.trim()
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-primary hover:bg-opacity-90",
                        isMobile && !content && "hidden"
                    )}
                >
                    {loading ? "发送中..." : "发送"}
                </button>
            </>
        );

        return (
            <div className={clsx(
                "absolute bottom-3 right-3 flex items-center space-x-2",
                isExpanded ? "bottom-4" : ""
            )}>
                {commonButtons}
            </div>
        );
    };
    return (
        <div className="h-screen flex flex-col bg-gray-50">
            {/* 顶部工具栏 */}

            <header className="w-full bg-white/80 backdrop-blur-sm border-b mb-3">
                <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                        <Link to="/" className="flex items-center space-x-2">
                            <img src="/logo.png" alt="口令树洞" className="h-8 w-8" />
                        </Link>

                        <div>
                            <span className="text-gray-700">{hole?.name}</span>
                            <button
                                onClick={() => setShowQRCode(true)}
                                className="p-1 text-gray-500 hover:text-gray-700"
                            >
                                <QrCodeIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-6 text-xs text-gray-500">
                                <div>
                                    <span title="有效消息">{hole?.valid_messages && fmtNumber(hole?.valid_messages)}</span>
                                    <span className="mx-1">/</span>
                                    <span title="总消息">{hole?.total_messages && fmtNumber(hole?.total_messages)}</span>
                                    <span >,</span>

                                    <span title="有效字节">{hole?.valid_bytes && fmtNumber(hole?.valid_bytes)}</span>
                                    <span className="mx-1">/</span>
                                    <span title="总字节">{hole?.total_bytes && fmtNumber(hole?.total_bytes)}</span>
                                </div>
                            </div>
                            {/* 
                            <select
                                value={sortType}
                                onChange={(e) => setSortType(e.target.value as SortType)}
                                className="text-sm border rounded px-2 py-1 text-gray-700"
                                style={{ width: '3rem' }}
                            >
                                <option value="created_asc">&uarr;&darr;</option>
                                <option value="created_desc">新消息</option>
                                <option value="expire_desc">最过期</option>
                                <option value="expire_asc">最早过期</option>
                            </select> */}

                            <button
                                onClick={() => fetchMessages()}
                                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"
                                title="刷新"
                            >
                                <ArrowPathIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                </div>
            </header>


            {/* 二维码弹窗 */}
            {showQRCode && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg">
                        <div className="mb-4" onClick={copyContent} title="点击复制链接">
                            <QRCodeSVG
                                value={window.location.href}
                                size={200}
                                level="H"
                                includeMargin={true}
                            />
                            <div className='text-center'>口令：{hole?.pwd}</div>
                            <div className='text-center'>截屏分享或扫码进入树洞</div>
                        </div>
                        <button
                            onClick={() => setShowQRCode(false)}
                            className="w-full py-2 bg-primary text-white rounded-lg"
                        >
                            关闭
                        </button>
                    </div>
                </div>
            )}

            {/* 消息列表区 */}
            <div className="overflow-hidden">
                <div
                    ref={containerRef}
                    className="overflow-y-auto px-4"
                    style={{ height: `${messageAreaHeight}px` }}
                >
                    {isLoadingMore && (
                        <div className="text-center py-2 text-gray-500">
                            加载中...
                        </div>
                    )}
                    <div className="max-w-4xl mx-auto space-y-4">
                        {messages.map((message) => (
                            <MessageItem
                                currentSelectExpirationMessageId={currentSelectExpirationMessageId}
                                key={message.id}
                                message={message}
                                onUpdateExpiration={(messageId, expireIn) =>
                                    updateMessageExpiration(holeId!, messageId, expireIn)
                                }
                                onClickExpiration={(messageId) =>
                                    setCurrentSelectExpirationMessageId(messageId)
                                }
                            />
                        ))}
                        <div ref={messagesEndRef} />
                        {isMobile && <div className="h-20" />}
                    </div>
                </div>

                {/* 分割线 */}
                <div
                    onMouseDown={handleMouseDown}
                    className="cursor-n-resize h-1 bg-gray-300"
                    style={{ height: "3px" }}
                />
            </div>

            {/* 输入区 */}
            <div
                className={clsx(
                    "border-ttt bg-white p-4 flex flex-col transition-all duration-300",
                    isExpanded ? "fixed inset-0 z-50" : ""
                )}
                style={{ height: isExpanded ? '100%' : `${inputAreaHeight}px` }}
            >
                <div className="max-w-4xl mx-auto relative flex flex-col flex-1 w-full">
                    {/* 新消息提示 */}
                    {showNewMessageButton && (
                        <button
                            onClick={handleNewMessageClick}
                            className="absolute right-10 bg-primary text-xxs text-white px-2 py-1 rounded-full shadow-lg
                        flex items-center space-x-2 hover:bg-opacity-90 transition-colors z-50"
                            style={{ top: '-1.5rem' }}
                        >
                            <ArrowDownIcon className="w-3 h-3" />
                            <span style={{ marginLeft: '3px' }}>有新消息</span>
                        </button>
                    )}
                    {/* 输入框 */}
                    <div className="relative flex-1">
                        {isCode ? (
                            <AceEditor
                                mode="c_cpp"
                                theme="monokai"
                                value={content}
                                onChange={(value) => setContent(value)}
                                className="border rounded-lg flex-1 w-full"
                                style={{ height: isExpanded ? '100%' : `${inputAreaHeight - 32}px` }}
                                width="100%"
                                height="600px"
                                fontSize={14}
                                showPrintMargin={false}
                                showGutter={true}
                                highlightActiveLine={true}
                                setOptions={{
                                    enableBasicAutocompletion: true,
                                    enableLiveAutocompletion: true,
                                    showLineNumbers: true,
                                    tabSize: 2,
                                }}
                            />
                        ) : (
                            <textarea
                                ref={textareaRef}
                                value={content}
                                onChange={handleInputChange}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                onKeyDown={handleKeyDown}
                                onPaste={handlePaste}
                                onDrop={handleDrop}
                                onDragOver={(e) => e.preventDefault()}
                                className={clsx(
                                    "w-full p-3 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary",
                                    isExpanded ? "h-full" : ""
                                )}
                                style={{
                                    height: isExpanded ? '100%' : `${calculateInputHeight(inputRows)}px`,
                                    paddingRight: content ? '6rem' : '3rem'
                                }}
                                placeholder="输入消息...(Ctrl + Enter 发送)"
                            />

                        )}

                        {/* 展开/收缩按钮 - 在所有设备上都显示 */}
                        {/* {(inputRows >= 3 || !isMobile) && ( */}
                        <button
                            onClick={toggleExpand}
                            className={clsx(
                                "absolute top-1 right-1 p-1",
                                isCode ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-700"
                            )}
                        >
                            {isExpanded ? (
                                <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                                <ChevronUpIcon className="w-4 h-4" />
                            )}
                        </button>
                        {/* )} */}
                    </div>
                    {/* 工具栏 */}
                    {renderToolbar()}
                </div>
            </div>
        </div>
    );
};

export default Chat; 